import * as React from "react";
import {
    Box,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Stack,
    Tooltip,
    Divider,
    Link,
    Typography,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility"; // your DetailsIcon

// helper: detect affiliate-ish links
const isAffiliate = (r) =>
    r?.is_aff_link ||
    r?.aff_link_on_dest ||
    (!!r?.affiliate_url && r?.affiliate_url !== "") ||
    r?.aff_link_in_redirects;

// helper: score -> "87%" (or "87.4%" if you prefer)
const pct = (n, dp = 0) => `${(n * 100).toFixed(dp)}%`;

export default function UrlList({
                                    report,
                                    onOpenDetails,            // (id) => void
                                    downloadingScreenshot = false,
                                    title = "URLs",
                                }) {
    const urls = report?.media?.urls ?? [];

    return (
        <Box sx={{ p: 1, mt: 2 }}>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                {title}
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                {urls.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            No URLs found for this report.
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding dense>
                        {urls.map((row, idx) => (
                            <React.Fragment key={row.id ?? idx}>
                                <ListItem alignItems="flex-start">
                                    {/* Left icon only if affiliate */}
                                    {isAffiliate(row) && (
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Tooltip title="Affiliate link detected">
                                                <MonetizationOnIcon color="warning" sx={{ opacity: 0.8 }} />
                                            </Tooltip>
                                        </ListItemIcon>
                                    )}

                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 6 }}>
                                                <Link
                                                    href={`https://1855proxy.xyz/?${row.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                    sx={{ fontWeight: 500, wordBreak: "break-all" }}
                                                >
                                                    {row.url}
                                                </Link>
                                                <OpenInNewIcon fontSize="inherit" />
                                                {/* Brands count chip (compact vs. full Alert) */}
                                                {row?.brands?.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label={`Brands: ${row.brands.length}`}
                                                    />
                                                )}
                                            </Stack>
                                        }
                                        secondary={
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                {(row.inference ?? []).map((m, i) => (
                                                    <Chip
                                                        key={`${m.label}-${i}`}
                                                        size="small"
                                                        label={`${m.label} (${pct(m.score)})`}
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {(!row.inference || row.inference.length === 0) && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No labels inferred
                                                    </Typography>
                                                )}
                                            </Stack>
                                        }
                                    />

                                    <ListItemSecondaryAction>
                                        <Tooltip title="Details">
                      <span>
                        <IconButton
                            edge="end"
                            onClick={() => onOpenDetails?.(row.id)}
                            disabled={downloadingScreenshot}
                            size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>

                                {idx < urls.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 2, mb: 5 }}>
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                    Some URLs may not be analyzed due to restrictions; others may reuse recent results.
                </Typography>
            </Stack>
        </Box>
    );
}
